import type { ImageProps } from '@hover/blueprint';
import { useTheme } from '@hover/blueprint';
import { useCallback, useMemo } from 'react';

import colorConsultation from 'src/assets/img/homeownerApp/modules/linkOuts/colorConsultation.png';
import colorConsultationInterior from 'src/assets/img/homeownerApp/modules/linkOuts/colorConsultationInterior.png';
import connectDesignerExterior from 'src/assets/img/homeownerApp/modules/linkOuts/connectDesignerExterior.png';
import connectDesignerInterior from 'src/assets/img/homeownerApp/modules/linkOuts/connectDesignerInterior.png';
import connectPro from 'src/assets/img/homeownerApp/modules/linkOuts/connectPro.jpg';
import pellaTrends from 'src/assets/img/homeownerApp/modules/linkOuts/pellaDesignTrends.png';
import pellaPro from 'src/assets/img/homeownerApp/modules/linkOuts/pellaPro.jpg';
import pellaPurchase from 'src/assets/img/homeownerApp/modules/linkOuts/pellaPurchase.png';
import pellaAdvice from 'src/assets/img/homeownerApp/modules/linkOuts/pellaTailoredAdvice.png';
import projectEstimates from 'src/assets/img/homeownerApp/modules/linkOuts/projectEstimates.png';
import projectEstimatesInterior from 'src/assets/img/homeownerApp/modules/linkOuts/projectEstimatesInterior.png';
import {
  alwaysCreateNewProject,
  closeUploadSheetAfterUpload,
} from 'src/modules/homeownerApp/components/UploadImage';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { usePartnerConfiguration } from 'src/modules/homeownerApp/hooks/usePartner';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { DeliverableType } from 'src/shared/constants/DeliverableType';
import type {
  BasicJobInfoFragment,
  Job,
  Project as ProjectType,
} from 'src/shared/graphql/generated';
import {
  JobImageViewpoints,
  JobModelStates,
  useGetScansQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { isMobileApp } from 'src/shared/utils/environment';

const ATTACHABLE_STATES = [JobModelStates.Complete, JobModelStates.Processing];
const ATTACHABLE_DELIVERABLES = [
  DeliverableType.COMPLETE,
  DeliverableType.ROOF_ONLY,
  DeliverableType.CAPTURE_ONLY,
];

export const useGetScans = () => {
  const { data, loading, error } = useGetScansQuery({
    variables: { archived: false },
  });

  if (loading) return { loading: true, scans: [] };

  if (error || !data) return { loading: false, scans: [] };

  return {
    loading: false,
    scans: data.jobs.results.filter(job => !job?.example) as Job[],
  };
};

export const useGetFailedScans = () => {
  const { data, loading, error } = useGetScansQuery();

  if (loading || error || !data) {
    return [];
  }

  return data.jobs.results.filter(job => job?.modelFailureReason) as Job[];
};

export const useGetFailedProjectScans = (project?: ProjectType) =>
  useMemo(
    () =>
      project?.jobs?.find(
        job => job?.state === JobModelStates.Failed || job?.modelFailureReason,
      ),
    [project],
  );

export const useAttachableScans = () => {
  const { data, loading, error } = useGetScansQuery();

  if (loading || error || !data) {
    return [];
  }

  return data.jobs.results.filter(
    job =>
      !job?.example &&
      job?.state &&
      job?.deliverable?.id &&
      ATTACHABLE_STATES.includes(job.state) &&
      ATTACHABLE_DELIVERABLES.includes(job?.deliverable?.id),
  ) as Job[];
};

export const useScanImage = (
  bestFrontImage: BasicJobInfoFragment['bestFrontImage'],
  images: BasicJobInfoFragment['images'],
  viewpoint: JobImageViewpoints = JobImageViewpoints.Exterior,
) => {
  const imagesFiltedByViewpoint = images.filter(
    image => image.viewpoint === viewpoint,
  );

  if (viewpoint !== JobImageViewpoints.Exterior) {
    return imagesFiltedByViewpoint[0]?.rotatedImageMediumUrl;
  }

  if (bestFrontImage) {
    return bestFrontImage.rotatedImageMediumUrl;
  }

  return imagesFiltedByViewpoint.find(image => image.cubeFace === 0)
    ?.rotatedImageMediumUrl;
};

export type LinkoutType = {
  buttonText: string;
  description: string;
  title: string;
  buttonHref: string;
  subtitle?: string;
  hideOnMobileApp?: boolean;
  defaultBackgroundColor?: string;
  imageSrc?: string;
  tagline?: string;
  imageProps?: Partial<ImageProps>;
  jumpLink?: string;
};

export const useAvailableProjectCards = ({
  project,
  isExteriorProject,
}: {
  project?: ProjectType;
  isExteriorProject?: boolean;
}) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { colors } = useTheme();

  const LINKOUT_META: { [key: string]: LinkoutType } = {
    connect_with_a_pro: {
      title: t('projects.links.connectPro.title'),
      subtitle: t('projects.links.connectPro.subtitle'),
      description: t('projects.links.connectPro.description'),
      buttonText: t('projects.links.connectPro.buttonText'),
      buttonHref: 'https://www.buildzoom.com/hover',
      hideOnMobileApp: false,
      imageSrc: connectPro,
      jumpLink: t('projects.links.connectPro.jumpLink'),
    },
    scan_for_measurements: {
      title: t('projects.links.scanForMeasurements.title'),
      description: t('projects.links.scanForMeasurements.description', {
        spaceType: isExteriorProject
          ? t('projects.links.scanForMeasurements.exterior')
          : t('projects.links.scanForMeasurements.interior'),
      }),
      buttonText: t('projects.links.scanForMeasurements.buttonText'),
      subtitle: t('projects.links.scanForMeasurements.subtitle'),
      buttonHref: '',
      hideOnMobileApp: false,
    },
    // general services
    get_an_estimate: {
      buttonText: t('projects.links.getAnEstimate.buttonText'),
      title: t('projects.links.getAnEstimate.title'),
      description: t('projects.links.getAnEstimate.description'),
      tagline: t('projects.links.getAnEstimate.tagline'),
      buttonHref:
        'https://app.mykukun.com/Home-Renovation-Costs?affiliate=hover',
      hideOnMobileApp: false,
      defaultBackgroundColor: isExteriorProject
        ? colors.neutral[600]
        : colors.orange[800],
      imageSrc: isExteriorProject ? projectEstimates : projectEstimatesInterior,
    },
    color_consultations: {
      title: t('projects.links.colorConsultations.title'),
      description: t('projects.links.colorConsultations.description'),
      buttonText: t('projects.links.colorConsultations.buttonText'),
      buttonHref: 'https://americascolorconsultants-hover.com',
      tagline: t('projects.links.colorConsultations.tagline'),
      hideOnMobileApp: false,
      defaultBackgroundColor: colors.blue[800],
      imageSrc: isExteriorProject
        ? colorConsultation
        : colorConsultationInterior,
    },
    custom_design_services: {
      title: t('projects.links.customDesignServices.title'),
      description: t('projects.links.customDesignServices.description'),
      buttonText: t('projects.links.customDesignServices.buttonText'),
      tagline: t('projects.links.customDesignServices.tagline'),
      buttonHref: 'https://hover.to/homeowner-designs',
      hideOnMobileApp: true,
      defaultBackgroundColor: isExteriorProject
        ? colors.orange[800]
        : colors.green[800],
      imageSrc: isExteriorProject
        ? connectDesignerExterior
        : connectDesignerInterior,
      imageProps: { marginX: 0 },
    },
    // Pella Services
    // Pella MEOW-5511
    pella_demystifying_windows: {
      title: t('projects.links.pellaDemystifying.title'),
      description: t('projects.links.pellaDemystifying.description'),
      buttonText: t('projects.links.pellaDemystifying.buttonText'),
      buttonHref:
        'https://www.pella.com/ideas/windows/?utm_source=hover&utm_medium=referral&pel_campaign_code=PEL432731264',
      tagline: t('projects.links.pellaDemystifying.subtitle'),
      hideOnMobileApp: false,
      defaultBackgroundColor: colors.neutral[500],
      imageSrc: pellaAdvice,
    },
    // Pella MEOW-5511
    pella_home_consultation: {
      buttonText: t('projects.links.pellaHomeConsultation.buttonText'),
      title: t('projects.links.pellaHomeConsultation.title'),
      description: t('projects.links.pellaHomeConsultation.description'),
      subtitle: t('projects.links.pellaHomeConsultation.tagline'),
      buttonHref:
        'https://www.pella.com/where-to-buy/schedule-consultation/?utm_source=hover&utm_medium=referral&pel_campaign_code=PEL827293654',
      hideOnMobileApp: false,
      imageSrc: pellaPro,
      jumpLink: t('projects.links.pellaHomeConsultation.jumpLink'),
      imageProps: { width: 700 },
    },
    // Pella MEOW-5511
    pella_find_retailer: {
      buttonText: t('projects.links.pellaFindRetailer.buttonText'),
      title: t('projects.links.pellaFindRetailer.title'),
      tagline: t('projects.links.pellaFindRetailer.tagline'),
      description: t('projects.links.pellaFindRetailer.description'),
      buttonHref:
        'https://www.pella.com/where-to-buy/?utm_source=hover&utm_medium=referral&pel_campaign_code=PEL172916149',
      hideOnMobileApp: false,
      defaultBackgroundColor: colors.blue[800],
      imageSrc: pellaPurchase,
    },
    // Pella MEOW-5511
    pella_design_trends: {
      buttonText: t('projects.links.pellaDesignTrends.buttonText'),
      title: t('projects.links.pellaDesignTrends.title'),
      description: t('projects.links.pellaDesignTrends.description'),
      tagline: t('projects.links.pellaDesignTrends.tagline'),
      buttonHref:
        'https://www.pella.com/ideas/design/2024-window-door-design-trends/?utm_source=hover&utm_medium=referral&pel_campaign_code=PEL248670246',
      hideOnMobileApp: false,
      defaultBackgroundColor: colors.neutral[600],
      imageSrc: pellaTrends,
      imageProps: { marginX: 0 },
    },
  };
  const configuration = usePartnerConfiguration();

  const availableLinkouts = project?.links?.concat(
    configuration?.additionalLinkouts ?? [],
  );

  const showComingSoon = !!project?.links?.includes('scan_for_measurements');

  const connectWithAProConfigs = [
    {
      name: 'pella_home_consultation',
      config: LINKOUT_META.pella_home_consultation,
    },
    {
      name: 'connect_with_a_pro',
      config: LINKOUT_META.connect_with_a_pro,
    },
  ].filter(
    ({ name, config }) =>
      !(isMobileApp() && config.hideOnMobileApp) &&
      availableLinkouts?.includes(name),
  );

  return {
    showComingSoon,
    scanForMeasurementsConfig: LINKOUT_META.scan_for_measurements,
    connectWithAProConfig:
      connectWithAProConfigs.length > 0
        ? connectWithAProConfigs[0].config
        : null,
    exploreServicesConfigs: [
      { name: 'get_an_estimate', config: LINKOUT_META.get_an_estimate },

      { name: 'color_consultations', config: LINKOUT_META.color_consultations },
      {
        name: 'custom_design_services',
        config: LINKOUT_META.custom_design_services,
      },
      { name: 'pella_find_retailer', config: LINKOUT_META.pella_find_retailer },
      { name: 'pella_design_trends', config: LINKOUT_META.pella_design_trends },
      {
        name: 'pella_demystifying_windows',
        config: LINKOUT_META.pella_demystifying_windows,
      },
    ].filter(
      ({ name, config }) =>
        !(isMobileApp() && config.hideOnMobileApp) &&
        availableLinkouts?.includes(name),
    ),
  };
};

export const useOpenNewProjectFlow = () => {
  const { setCurrentMenu } = useHomeownerAppContext();

  const openUploadSheetFromFloatingNav = useCallback(
    (newProject: boolean = false) => {
      closeUploadSheetAfterUpload(!newProject);
      alwaysCreateNewProject(newProject);
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
    },
    [setCurrentMenu],
  );

  return openUploadSheetFromFloatingNav;
};
